import { ENTITY_ICONS } from "@/constants/entity-icons"
import {
  Bell,
  BotMessageSquare,
  ClipboardCheck,
  FileText,
  Gauge,
  Home,
  LineChart,
  Mic,
  Ruler,
  Search,
  SlidersHorizontal,
  Volume2,
  Workflow,
} from "lucide-react"

import { MessageSquareArrowUp } from "@/components/icons/MessageSquareArrowUp"

import type { NavigationConfigItem } from "../types"
import { HOME_BASE_HREF, UPLOAD_CONVERSATION_BASE_HREF } from "./siteNavigation"

/**
 * The sidebar grouped around the agent improvement loop, behind
 * `FEATURE_FLAGS.IMPROVEMENT_LOOP_NAV`.
 *
 * The shipped nav lists Dashboards, Metrics, Reports, Alerts, and Simulations
 * as peers, so its order carries no claim about how the product is used. This
 * one names the phases of the loop instead — the conversations a change
 * produced, what judges them, explore what they did, configure what runs next —
 * and hangs each destination under the phase it belongs to. The first section is
 * named for the material it holds, and its children for where those
 * conversations came from: simulated by Coval, or uploaded from production. The
 * second holds the two ways a conversation gets judged, automatically by a
 * metric or by a person.
 *
 * Sections are grouping labels rather than links: `isSection` marks them so the
 * builder keeps a section whose own href is out of reach while its children are
 * not, and so a section can host children that are themselves owned elsewhere
 * (Dashboards brings its own).
 *
 * The compact sidebar renders one icon per top-level item and no children, so a
 * section's own `base_href` is where its icon leads: "Conversations" opens
 * Simulated, and the rest of its children are reachable only once the sidebar
 * expands.
 *
 * It lives beside `siteNavigation` rather than replacing it because both have to
 * render until the flag is retired. When it is, this becomes the nav and that
 * file's `navItems` goes.
 */
export const IMPROVEMENT_LOOP_NAV_ITEMS: NavigationConfigItem[] = [
  {
    label: "Home",
    base_href: HOME_BASE_HREF,
    icon: Home,
    keywords: ["home", "dashboard", "overview", "coval"],
  },
  {
    label: "Conversations",
    base_href: "/simulated",
    icon: Workflow,
    isSection: true,
    keywords: [
      "conversations",
      "simulate",
      "observe",
      "upload",
      "transcripts",
      "loop",
    ],
    subItems: [
      {
        label: "Simulated",
        base_href: "/simulated",
        createBaseHref: "/simulate",
        icon: BotMessageSquare,
        keywords: [
          "simulated",
          "view",
          "runs",
          "simulations",
          "simulate",
          "conversations",
        ],
      },
      {
        label: "Uploaded",
        base_href: "/uploaded",
        createBaseHref: UPLOAD_CONVERSATION_BASE_HREF,
        createLabel: "Upload conversation",
        icon: MessageSquareArrowUp,
        keywords: [
          "uploaded",
          "conversations",
          "live",
          "observe",
          "observability",
          "monitoring",
        ],
      },
    ],
  },
  {
    label: "Measure",
    base_href: "/metrics",
    icon: Ruler,
    isSection: true,
    keywords: ["measure", "metrics", "evaluate", "review", "score", "judge"],
    subItems: [
      {
        label: "Metrics",
        base_href: "/metrics",
        createBaseHref: "/metrics?create=true",
        icon: ENTITY_ICONS.metrics,
        keywords: [
          "metrics",
          "workflows",
          "tool call",
          "binary",
          "judge",
          "llm",
        ],
      },
      {
        label: "Human Review",
        base_href: "/review/projects",
        icon: ClipboardCheck,
        keywords: ["review", "human", "feedback", "runs"],
      },
    ],
  },
  {
    label: "Explore",
    base_href: "/dashboard",
    icon: LineChart,
    isSection: true,
    keywords: ["explore", "dashboards", "reports", "traces"],
    subItems: [
      {
        label: "Dashboards",
        base_href: "/dashboard",
        icon: LineChart,
        keywords: ["simulations", "conversations", "dashboard"],
      },
      {
        label: "Reports",
        base_href: "/reports",
        icon: FileText,
        keywords: ["reports", "saved", "multi-run"],
      },
      {
        label: "Trace Search",
        base_href: "/traces",
        icon: Search,
        keywords: ["traces", "search", "otel", "opentelemetry", "spans"],
      },
    ],
  },
  {
    label: "Configure",
    base_href: "/agents",
    icon: SlidersHorizontal,
    isSection: true,
    keywords: ["agents", "personas", "test sets", "alerts"],
    subItems: [
      {
        label: "Agents",
        base_href: "/agents",
        createBaseHref: "/agents?create=true",
        icon: ENTITY_ICONS.agents,
        keywords: ["agents"],
      },
      {
        label: "Personas",
        base_href: "/personas",
        createBaseHref: "/personas/new",
        icon: ENTITY_ICONS.personas,
        keywords: ["personas", "simulators", "voice ids"],
      },
      {
        label: "Test Sets",
        base_href: "/test-sets",
        createBaseHref: "/test-sets/create",
        icon: ENTITY_ICONS["test-sets"],
        keywords: ["test", "evaluate", "coval", "datasets"],
      },
      {
        label: "Alerts",
        base_href: "/alerts",
        createBaseHref: "/alerts/create",
        icon: Bell,
        keywords: ["alerts", "monitors", "thresholds", "notifications"],
      },
    ],
  },
  {
    label: "Benchmarks",
    base_href: "/benchmarks",
    icon: Gauge,
    isSection: true,
    featureFlag: "benchmarks",
    keywords: ["benchmark", "stt", "tts", "performance", "wer", "ttfc"],
    subItems: [
      {
        label: "STT",
        base_href: "/benchmarks/stt",
        icon: Mic,
        keywords: ["stt", "speech to text", "transcription", "wer"],
      },
      {
        label: "TTS",
        base_href: "/benchmarks/tts",
        icon: Volume2,
        keywords: ["tts", "text to speech", "synthesis", "ttfc"],
      },
    ],
  },
]
